import { ApiError } from "@/api/client";

/** The ONE way a failed write is shown.
 *
 *  There were three. `useMutationFeedback` (a red span, used by the Asset
 *  Catalog), a hand-rolled `{m.isError && <div className="panel panel-pad">…}`
 *  in eleven places across Products and Organisations, and a red-bordered
 *  panel I added to the spec editor when a refusal proved unreadable. Three
 *  presentations of one event, so the same failure looked different depending
 *  on which page you were standing on — which is what Dattatray kept reporting
 *  as the modules not feeling like one product.
 *
 *  UNWRAPPING LIVES HERE TOO. `ApiError` carries a code, a message and
 *  sometimes `detail.errors[]` (the catalogue returns one entry per broken
 *  rule). Formatting that in each caller is how one page shows "CONFLICT" and
 *  the next shows the sentence a person can act on.
 *
 *  TWO PRESENTATIONS, ONE SOURCE:
 *
 *    inline (default)  a red line, for a failure next to the control
 *    panel             a bordered, tinted block for a failure that must not
 *                      be missed — a long form where the button is far from
 *                      the content, which is exactly how a refused spec save
 *                      went unread for hours
 */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    return [e.message || e.code, ...(e.detail?.errors ?? [])].join(" · ");
  }
  return e instanceof Error ? e.message : String(e ?? "");
}

export function ErrorNote({ error, panel = false, lead = "Not saved." }: {
  /** the mutation's `error`; nothing renders when it is null/undefined */
  error: unknown;
  /** bordered block instead of a line — use where the control is far away */
  panel?: boolean;
  /** the bold opener on the panel form */
  lead?: string;
}) {
  if (!error) return null;
  const text = errorText(error);

  if (!panel) {
    return <span style={{ color: "var(--bad)", fontSize: 12 }}>{text}</span>;
  }
  return (
    <div className="panel panel-pad" style={{
      borderColor: "var(--bad)",
      background: "color-mix(in srgb, var(--bad) 8%, transparent)",
      color: "var(--bad)",
      marginTop: 10, marginBottom: 10,
    }}>
      <strong>{lead}</strong> {text}
    </div>
  );
}
